import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShoppingCart, 
  faStar, 
  faStarHalfAlt, 
  faTruck, 
  faShieldAlt, 
  faUndo 
} from '@fortawesome/free-solid-svg-icons';
import { Product } from '../../utils/types';
import { theme } from '../../styles/theme';
import { Button, Badge } from '../../styles/components';
import { useCart } from '../../context/CartContext';

interface ProductDetailProps {
  product: Product;
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};
  margin: ${theme.spacing.xl} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
    margin: ${theme.spacing.lg} 0;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.md};
    margin: ${theme.spacing.md} 0;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: row;
    order: 2;
  }
`;

const Thumbnail = styled.div<{ active: boolean }>`
  width: 100%;
  height: 80px;
  border-radius: ${theme.borderRadius.small};
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${({ active }) => (active ? theme.colors.primary : 'transparent')};
  opacity: ${({ active }) => (active ? 1 : 0.7)};
  transition: all 0.2s;
  
  &:hover {
    opacity: 1;
    border-color: ${theme.colors.primary};
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 80px;
  }
`;

const MainImage = styled.div`
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
  height: 500px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 400px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const Title = styled.h1`
  font-family: ${theme.fonts.heading};
  font-size: 2.5rem;
  margin-bottom: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const CategoryName = styled.div`
  color: ${theme.colors.gray};
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: ${theme.spacing.sm};
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  
  svg {
    color: ${theme.colors.primary};
    margin-right: 2px;
  }
  
  .rating-text {
    margin-left: ${theme.spacing.xs};
    font-size: 0.875rem;
    color: ${theme.colors.darkGray};
  }
  
  .reviews-count {
    margin-left: ${theme.spacing.sm};
    color: ${theme.colors.darkGray};
    font-size: 0.875rem;
  }
`;

const Description = styled.div`
  margin-bottom: ${theme.spacing.md};
  line-height: 1.6;
`;

const Price = styled.div`
  font-size: 1.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.md} 0;
  
  .original-price {
    text-decoration: line-through;
    color: ${theme.colors.gray};
    font-weight: 400;
    font-size: 1.25rem;
  }
  
  .discounted-price {
    color: ${theme.colors.primary};
  }
  
  .discount-badge {
    font-size: 0.875rem;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
    border-radius: ${theme.borderRadius.small};
    font-weight: 600;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  
  button {
    width: 40px;
    height: 40px;
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.lightGray};
    border: none;
    font-size: 1.25rem;
    font-weight: bold;
    cursor: pointer;
    
    &:hover {
      background-color: ${theme.colors.gray};
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
  
  span {
    width: 60px;
    text-align: center;
    font-size: 1.125rem;
    font-weight: 500;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.lg} 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.sm};
  }
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.medium};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: row;
    text-align: left;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  
    svg {
      margin-right: ${theme.spacing.md};
      margin-bottom: 0;
    }
    
    div {
      flex: 1;
    }
  }
  
  svg {
    font-size: 1.5rem;
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.secondary};
  }
  
  h4 {
    margin-bottom: ${theme.spacing.xs};
    font-weight: 600;
  }
  
  p {
    font-size: 0.875rem;
    color: ${theme.colors.darkGray};
  }
`;

const Tags = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin-top: ${theme.spacing.md};
  
  span {
    background-color: ${theme.colors.lightGray};
    color: ${theme.colors.darkGray};
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.small};
    font-size: 0.875rem;
  }
`;

const ProductDetail: React.FC<ProductDetailProps> = ({ product }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  
  // Function to render stars based on rating
  const renderRatingStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FontAwesomeIcon key={`full-${i}`} icon={faStar} />);
    }
    
    // Add half star if needed
    if (hasHalfStar) {
      stars.push(<FontAwesomeIcon key="half" icon={faStarHalfAlt} />);
    }
    
    return stars;
  };
  
  // Calculate discount percentage
  const calculateDiscount = () => {
    if (!product.discountPrice) return null;
    
    const discount = ((product.price - product.discountPrice) / product.price) * 100;
    return Math.round(discount);
  };
  
  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.discountPrice || product.price,
      image: product.images[0],
      quantity,
      category: product.category,
    });
  };
  
  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };
  
  const discount = calculateDiscount();
  
  return (
    <Container>
      <ImageGallery>
        <ThumbnailsContainer>
          {product.images.map((image, index) => (
            <Thumbnail 
              key={index} 
              active={selectedImage === index}
              onClick={() => setSelectedImage(index)}
            >
              <img src={image} alt={`${product.name} ${index + 1}`} />
            </Thumbnail>
          ))}
        </ThumbnailsContainer>
        
        <MainImage>
          <img src={product.images[selectedImage]} alt={product.name} />
        </MainImage>
      </ImageGallery>
      
      <ProductInfo>
        <CategoryName>{product.category.replace('-', ' ')}</CategoryName>
        <Title>{product.name}</Title>
        
        {product.rating && (
          <Rating>
            {renderRatingStars(product.rating)}
            <span className="rating-text">{product.rating.toFixed(1)}</span>
            {product.reviews && (
              <span className="reviews-count">
                ({product.reviews.length} {product.reviews.length === 1 ? 'review' : 'reviews'})
              </span>
            )}
          </Rating>
        )}
        
        <Description>{product.description}</Description>
        
        <Price>
          {product.discountPrice ? (
            <>
              <span className="discounted-price">${product.discountPrice.toFixed(2)}</span>
              <span className="original-price">${product.price.toFixed(2)}</span>
              {discount && <span className="discount-badge">{discount}% OFF</span>}
            </>
          ) : (
            <span>${product.price.toFixed(2)}</span>
          )}
        </Price>
        
        <div>
          <Badge type={product.inStock ? 'success' : 'error'}>
            {product.inStock ? 'In Stock' : 'Out of Stock'}
          </Badge>
        </div>
        
        {product.inStock && (
          <QuantitySelector>
            <button 
              onClick={() => handleQuantityChange(-1)}
              disabled={quantity <= 1}
            >
              -
            </button>
            <span>{quantity}</span>
            <button onClick={() => handleQuantityChange(1)}>+</button>
          </QuantitySelector>
        )}
        
        <Actions>
          <Button 
            onClick={handleAddToCart}
            disabled={!product.inStock}
          >
            <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
          </Button>
        </Actions>
        
        {product.tags && (
          <Tags>
            {product.tags.map((tag, index) => (
              <span key={index}>{tag}</span>
            ))}
          </Tags>
        )}
        
        <Features>
          <FeatureItem>
            <FontAwesomeIcon icon={faTruck} />
            <h4>Fast Delivery</h4>
            <p>Same day delivery in Manama</p>
          </FeatureItem>
          <FeatureItem>
            <FontAwesomeIcon icon={faShieldAlt} />
            <h4>Quality Guarantee</h4>
            <p>100% authentic products</p>
          </FeatureItem>
          <FeatureItem>
            <FontAwesomeIcon icon={faUndo} />
            <h4>Easy Returns</h4>
            <p>30 days return policy</p>
          </FeatureItem>
        </Features>
      </ProductInfo>
    </Container>
  );
};

export default ProductDetail; 